import { EVENTS } from 'util.events'

class ToggleMenu extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    this.isMobileNavOpen = false
    // Trigger to open header nav
    this.openTrigger = this.querySelector('[aria-controls="MobileNav"]')

    if (!this.openTrigger) return
    // Open/close mobile nav
    this.openTrigger.addEventListener('click', this.handleClick.bind(this), { signal: this.abortController.signal })

    // Drawer actions
    document.addEventListener(EVENTS.headerDrawerClosed, this.handleDrawerClosed.bind(this), {
      signal: this.abortController.signal
    })

    // RareSeeds 2026-10-16 (KNOWN-OPEN-1): a tap recorded before hydration by the inline script in
    // snippets/header.toggle-menu.liquid. Replayed HERE, not on whenDefined('toggle-menu'): is-land
    // keeps this element detached until the header island resolves, so connect is the first moment
    // the click listener above exists. And only once header-drawer is defined too -- toggle-menu
    // landed 80 ms before it on a cold PLP and the open event went to nobody.
    if (this.openTrigger.hasAttribute('data-rs-pending-open')) this.replayPendingOpen()
  }

  replayPendingOpen() {
    // header-section too: HeaderSection.connectedCallback (assets/section.header.js) dispatches
    // mobileNav:close at >=769px the moment it connects, and its define order against
    // header-drawer is not fixed (the island imports it asynchronously; measured both ways). A
    // replay that lands between the two opens the drawer and watches it close again. Both promises
    // settle only after the respective connectedCallbacks have run, so the replay is the last word.
    Promise.all([customElements.whenDefined('header-drawer'), customElements.whenDefined('header-section')]).then(() => {
      if (!this.isConnected || !this.openTrigger.hasAttribute('data-rs-pending-open')) return
      this.openTrigger.removeAttribute('data-rs-pending-open')
      // already open (a later tap went the normal route): never toggle it closed from here
      const drawer = document.querySelector('header-drawer.site-header__mobile-nav')
      if (drawer && drawer.classList.contains('is-active')) return
      // reset so the replayed click reads as a fresh "open" (handleClick sets it true again)
      this.openTrigger.setAttribute('aria-expanded', 'false')
      this.openTrigger.click()
    })
  }

  handleClick(evt) {
    evt.preventDefault()
    // RareSeeds 2026-10-16: drawer module not here yet (cold-load race, toggle-menu defined before
    // header-drawer): keep the tap and replay it when the drawer can hear it.
    if (!customElements.get('header-drawer')) {
      this.openTrigger.setAttribute('data-rs-pending-open', '')
      this.openTrigger.setAttribute('aria-expanded', 'true')
      this.replayPendingOpen()
      return
    }
    this.openTrigger.removeAttribute('data-rs-pending-open')
    // RareSeeds 2026-10-12: decide from the drawer's REAL state, not the remembered flag. The flag
    // only resets on headerDrawerClosed; if that event was ever missed, every later tap sent
    // "close" to a closed drawer and the menu could not be opened without a refresh.
    const drawer = document.querySelector('header-drawer.site-header__mobile-nav')
    if (drawer) this.isMobileNavOpen = drawer.classList.contains('is-active')
    // RareSeeds a11y 2026-10-16: dispatch at the BUTTON, not evt.target. A tap on the glyph itself
    // made evt.target the <svg>, HeaderDrawer stored that as the element to return focus to, and
    // an <svg> cannot take focus -- so Esc dropped focus to <body> (3/3 on the PDP at 390).
    if (this.isMobileNavOpen) {
      this.openTrigger.dispatchEvent(new CustomEvent(EVENTS.mobileNavClose, { bubbles: true }))
      this.openTrigger.classList.remove('is-active')
      // this.classList.remove('mobile-nav-open')
      this.isMobileNavOpen = false
      this.openTrigger.setAttribute('aria-expanded', String(this.isMobileNavOpen))
    } else {
      this.openTrigger.dispatchEvent(new CustomEvent(EVENTS.mobileNavOpen, { bubbles: true }))
      this.openTrigger.classList.add('is-active')
      // this.classList.add('mobile-nav-open')
      this.isMobileNavOpen = true
      this.openTrigger.setAttribute('aria-expanded', String(this.isMobileNavOpen))
    }
  }

  handleDrawerClosed() {
    this.isMobileNavOpen = false
    this.openTrigger.classList.remove('is-active')
    // launch readiness 2026-09-24: keep aria-expanded in step with the drawer for screen readers.
    this.openTrigger.setAttribute('aria-expanded', String(this.isMobileNavOpen))
  }

  disconnectedCallback() {
    this.abortController.abort()
  }
}

customElements.define('toggle-menu', ToggleMenu)
